import type { MenuProps } from 'antd';
import type { RoomUser } from '@/contexts/chat/scripts/hooks/useQuickVO/types';
import {
  MediaDeviceKind,
  MediaType,
  type UseQuickVOResult,
} from '@/contexts/chat/scripts/hooks/useQuickVO/types';
import styles from './index.module.scss';

const AudioOperate = ({
  open,
  children,
  devicesData,
  roomUser,
  publish,
  inactiveTracks,
  getMediaDeviceKind,
  setMediaDeviceKind,
}: {
  open: boolean;
  children: ReactNode;
  devicesData: MediaDeviceInfo[];
  roomUser: RoomUser | undefined;
  publish: UseQuickVOResult['publish'];
  inactiveTracks: UseQuickVOResult['inactiveTracks'];
  getMediaDeviceKind: UseQuickVOResult['getMediaDeviceKind'];
  setMediaDeviceKind: UseQuickVOResult['setMediaDeviceKind'];
}) => {
  const [activeKey, setActiveKey] = useState('');
  const [devices, setDevices] = useState<MenuProps['items']>([]);

  const defaultItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'microphone',
        label: <div className={styles.menuLabel}>Microphone</div>,
        disabled: true,
      },
      {
        type: 'divider',
      },
    ],
    []
  );

  const getAudioInputDevices = useCallback(
    async (devicesData: MediaDeviceInfo[]) => {
      if (activeKey && open) {
        if (devicesData.find((device) => device.deviceId === activeKey)) {
          await setMediaDeviceKind(MediaDeviceKind.AUDIO_INPUT, activeKey)?.catch(
            (_) => {
              message.warning('Device switching failed');
            }
          );
          inactiveTracks(MediaType.MICROPHONECAMERA_AUDIO, true);
        } else {
          inactiveTracks(MediaType.MICROPHONECAMERA_AUDIO, false);
        }
      }

      setDevices([
        ...defaultItems,
        ...devicesData!.map((device) => ({
          kind: device.kind,
          key: device.deviceId,
          label: <div className={styles.menuLabel}>{device.label}</div>,
        })),
      ]);
    },
    [activeKey, defaultItems, inactiveTracks, open, setMediaDeviceKind]
  );

  useEffect(() => {
    if (devicesData.length) {
      getAudioInputDevices(devicesData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devicesData.length]);

  useEffect(() => {
    if (open && devicesData.length) {
      const deviceId = getMediaDeviceKind(MediaDeviceKind.AUDIO_INPUT);
      if (devicesData.find((device) => device.deviceId === deviceId)) {
        setActiveKey(deviceId!);
      } else {
        setMediaDeviceKind(MediaDeviceKind.AUDIO_INPUT, 'default');
        setActiveKey('default');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMediaDeviceKind, open, setMediaDeviceKind]);

  return (
    <Dropdown
      overlayClassName={styles.dropdownwrapper}
      menu={{
        items: devices,
        activeKey,
        onClick: async (item) => {
          try {
            setActiveKey(item.key);
            await setMediaDeviceKind(MediaDeviceKind.AUDIO_INPUT, item.key);
            if (
              !roomUser?.tracks?.find(
                (track) => track.mediaType === MediaType.MICROPHONECAMERA_AUDIO
              )
            ) {
              await publish(MediaType.MICROPHONECAMERA_AUDIO);
            }
            if (!roomUser?.callActionMap.microphoneCamera_audio) {
              inactiveTracks(MediaType.MICROPHONECAMERA_AUDIO, true);
            }
          } catch (error) {
            message.warning('Device switching failed');
            console.log(error, 'error');
          }
        },
      }}
      placement="top"
      getPopupContainer={(e) => e.parentElement as HTMLDivElement}
    >
      <div>{children}</div>
    </Dropdown>
  );
};
export default AudioOperate;
